// The injectable frame-scheduler + clock seams for the canvas RAF render loop
// (design D8, task 1.1). The render loop schedules its next frame through a
// `FrameScheduler` and reads wall-clock time through a `Clock`, so the loop's
// lifecycle (idempotent init/destroy, cancellation), interpolation timing, and the
// degradation-mode hysteresis window are all deterministically testable without a GPU
// or a real `requestAnimationFrame` — the fakes live in `src/test/`
// (`FakeFrameScheduler` / `FakeClock`). Production wires the real
// `requestAnimationFrame` + `performance.now` adapters exported below.

/** Schedules and cancels animation-frame callbacks. `schedule` returns an opaque
 * handle that `cancel` accepts; a scheduled callback runs at most once. */
export interface FrameScheduler {
  schedule(callback: () => void): number;
  cancel(handle: number): void;
}

/** A wall-clock time source in milliseconds — the loop's decorative hover-bob and its
 * interpolation target are functions of `now()`, so it is injectable to drive time
 * deterministically in tests. */
export interface Clock {
  now(): number;
}

/** The production `FrameScheduler`: a thin adapter over `requestAnimationFrame` /
 * `cancelAnimationFrame`. The RAF timestamp is deliberately ignored — the loop reads
 * time from the injected `Clock`, keeping the two seams independent. Referenced only
 * inside the methods, so importing this module never touches `requestAnimationFrame`
 * (absent in the node test environment). */
export const rafFrameScheduler: FrameScheduler = {
  schedule: (callback) => requestAnimationFrame(() => callback()),
  cancel: (handle) => cancelAnimationFrame(handle),
};

/** The production `Clock`: `performance.now()`. */
export const performanceClock: Clock = {
  now: () => performance.now(),
};
